<script setup lang="ts">
  import { computed } from 'vue';
  import * as z from 'zod';
  import { useModal } from 'vue-final-modal';
  import { List } from 'lucide-vue-next';
  import { usePropertyForm } from '../../../model/usePropertyForm';
  import type { RadioGroupProperties as RadioGroupPropsType } from '../../../model/form.types';
  import { FORM_FIELD_CHARACTER_LIMITS } from '../../../lib/form-field-character-limits';
  import {
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
    Input,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Switch,
  } from '@/shared/ui';
  import RadioGroupAlternativesModal from './RadioGroupAlternativesModal.vue';

  interface Props {
    elementId?: string;
  }

  const props = defineProps<Props>();

  type FormValues = {
    name: string;
    helperText?: string;
    label?: string;
    labelPosition?: 'top' | 'left';
    required?: boolean;
    orientation?: 'vertical' | 'horizontal';
    options: string[];
  };

  const validationSchema = z.object({
    name: z.string().min(1, 'O nome é obrigatório').max(FORM_FIELD_CHARACTER_LIMITS.FIELD_ID),
    helperText: z.string().max(FORM_FIELD_CHARACTER_LIMITS.AUXILIARY_TEXT).optional(),
    label: z.string().max(FORM_FIELD_CHARACTER_LIMITS.LABEL).optional(),
    labelPosition: z.enum(['top', 'left']).optional(),
    required: z.boolean().optional(),
    orientation: z.enum(['vertical', 'horizontal']).optional(),
    options: z.array(z.string()),
  });

  const { selectedElement } = usePropertyForm<FormValues, RadioGroupPropsType>({
    elementId: () => props.elementId,
    validationSchema,
    extractInitialValues: (properties) => ({
      name: properties?.name ?? '',
      helperText: properties?.helperText ?? '',
      label: properties?.label ?? '',
      labelPosition: properties?.labelPosition ?? 'top',
      required: properties?.required ?? false,
      orientation: properties?.orientation ?? 'vertical',
      options: properties?.options ?? ['Opção 1', 'Opção 2'],
    }),
    mapValuesToProperties: (values, currentProperties) => {
      const rest = { ...values };
      delete (rest as { options?: unknown }).options;
      return {
        properties: {
          ...currentProperties,
          ...rest,
          name: values.name || currentProperties.name,
        },
      };
    },
  });

  const alternativesCount = computed(
    () => (selectedElement.value?.properties as RadioGroupPropsType)?.options?.length ?? 0
  );

  const alternativesModal = useModal({
    component: RadioGroupAlternativesModal,
    attrs: {
      elementId: props.elementId ?? '',
      onClose() {
        alternativesModal.close();
      },
    },
  });
</script>

<template>
  <div class="flex flex-col">
    <div class="border-b border-gray-02 py-4">
      <FormField
        v-slot="{ componentField }"
        name="name"
      >
        <FormItem class="flex flex-col gap-2">
          <FormLabel class="text-sm text-gray-11 font-normal">
            ID do campo
          </FormLabel>
          <FormControl>
            <Input
              v-bind="componentField"
              placeholder="nome_do_campo"
              class="h-11"
              :maxlength="FORM_FIELD_CHARACTER_LIMITS.FIELD_ID"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>
    </div>

    <div class="border-b border-gray-02 py-4">
      <FormField
        v-slot="{ componentField }"
        name="helperText"
      >
        <FormItem class="flex flex-col gap-2">
          <FormLabel class="text-sm text-gray-11 font-normal">
            Texto auxiliar
          </FormLabel>
          <FormControl>
            <Input
              v-bind="componentField"
              placeholder="Digite aqui o texto auxiliar"
              class="h-11"
              :maxlength="FORM_FIELD_CHARACTER_LIMITS.AUXILIARY_TEXT"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>
    </div>

    <div class="border-b border-gray-02 py-4 flex flex-col gap-[10px]">
      <FormField
        v-slot="{ componentField }"
        name="label"
      >
        <FormItem class="flex flex-col gap-2">
          <FormLabel class="text-sm text-gray-11 font-normal">
            Rótulo
          </FormLabel>
          <FormControl>
            <Input
              v-bind="componentField"
              placeholder="Digite aqui"
              class="h-[42px]"
              :maxlength="FORM_FIELD_CHARACTER_LIMITS.LABEL"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>

      <FormField
        v-slot="{ componentField }"
        name="labelPosition"
      >
        <FormItem class="flex flex-col gap-2">
          <FormLabel class="text-sm text-gray-11 font-normal">
            Posicionamento
          </FormLabel>
          <Select v-bind="componentField">
            <FormControl>
              <SelectTrigger class="h-11 w-full">
                <SelectValue placeholder="Selecione" />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              <SelectItem value="top">
                Acima do campo
              </SelectItem>
              <SelectItem value="left">
                À Esquerda do Campo
              </SelectItem>
            </SelectContent>
          </Select>
          <FormMessage />
        </FormItem>
      </FormField>
    </div>

    <div class="border-b border-gray-02 py-4">
      <FormField
        v-slot="{ componentField }"
        name="required"
      >
        <FormItem class="flex items-center justify-between h-5">
          <FormLabel class="text-sm text-gray-07 font-normal cursor-pointer">
            Obrigatória
          </FormLabel>
          <FormControl>
            <Switch v-bind="componentField" />
          </FormControl>
        </FormItem>
      </FormField>
    </div>

    <div class="flex gap-2 items-center h-[42px] py-2 border-b border-gray-03 mt-4">
      <div class="bg-gray-03 flex items-center justify-center p-0.5 rounded shrink-0 size-6">
        <List
          :size="18"
          class="text-white-01"
        />
      </div>
      <span class="text-sm text-gray-07">Alternativas</span>
    </div>

    <div class="py-4 flex flex-col gap-4">
      <div class="flex flex-col gap-2">
        <button
          type="button"
          class="w-full h-[42px] bg-[var(--theme-secondary)] text-white text-sm font-medium rounded-lg flex items-center justify-center shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]"
          @click="alternativesModal.open()"
        >
          Editar alternativas
        </button>
        <p class="text-[10px]">
          <span class="text-gray-07">Total de alternativas: </span>
          <span class="text-gray-03">{{ alternativesCount }}</span>
        </p>
      </div>

      <FormField
        v-slot="{ componentField }"
        name="orientation"
      >
        <FormItem class="flex flex-col gap-2">
          <FormLabel class="text-sm text-gray-11 font-normal">
            Organização
          </FormLabel>
          <Select v-bind="componentField">
            <FormControl>
              <SelectTrigger class="h-11 w-full">
                <SelectValue placeholder="Selecione" />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              <SelectItem value="horizontal">
                Horizontal
              </SelectItem>
              <SelectItem value="vertical">
                Vertical
              </SelectItem>
            </SelectContent>
          </Select>
          <FormMessage />
        </FormItem>
      </FormField>
    </div>
  </div>

  <FormField name="options">
    <FormMessage />
  </FormField>
</template>
